/**
 * Turning a flat message list into something that reads like a
 * conversation: runs of consecutive messages from one sender, and a day
 * break where the calendar moves on.
 *
 * Shared because the rules must be IDENTICAL across the landing widget,
 * the portal widget and the operator console — they render the same
 * thread, and a run that groups on one surface and not another makes the
 * same conversation look like two different ones. (Mobile carries its own
 * copy: it has no dependency on this package and Metro resolution for a
 * workspace package is not worth the risk for forty lines.)
 *
 * Pure and date-injectable so it can be tested without freezing the clock.
 */

export type ChatGroupable = {
  id: string;
  authorKind: string;
  authorUserId?: string | null;
  /** ISO timestamp. */
  createdAt: string;
};

export type ChatRow<T extends ChatGroupable> = {
  message: T;
  /** First of a run: this is where the sender's name belongs. */
  startsGroup: boolean;
  /** Last of a run: this is where the timestamp belongs. */
  endsGroup: boolean;
  /** Day label to render ABOVE this message, when the day changed. */
  daySeparator: string | null;
};

/**
 * Messages further apart than this start a new run even from the same
 * sender. Two answers seconds apart are one thought; an answer an hour
 * later is a new one, and collapsing them hides that the customer waited.
 */
const GROUP_GAP_MS = 5 * 60_000;

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** "Hoy" / "Ayer" / "12 de agosto" (with the year once it is not this one). */
export function dayLabel(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (isSameDay(date, now)) return "Hoy";
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameDay(date, yesterday)) return "Ayer";
  return date.toLocaleDateString("es-UY", {
    day: "numeric",
    month: "long",
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  });
}

/**
 * Annotate messages with grouping and day breaks. Input must be sorted
 * oldest-first, which is how every caller already holds it.
 *
 * System markers ("X se sumó a la conversación") never join a run in
 * either direction: they are about the conversation rather than a turn in
 * it, and letting one sit inside a run would silently swallow the name
 * label of the messages after it.
 */
export function groupChatMessages<T extends ChatGroupable>(
  messages: T[],
  now: Date = new Date(),
): ChatRow<T>[] {
  return messages.map((message, i) => {
    const previous = i > 0 ? messages[i - 1] : undefined;
    const next = i < messages.length - 1 ? messages[i + 1] : undefined;

    const daySeparator =
      previous === undefined ||
      !isSameDay(new Date(previous.createdAt), new Date(message.createdAt))
        ? dayLabel(message.createdAt, now)
        : null;

    const continues = (a: T | undefined, b: T): boolean => {
      if (!a) return false;
      if (a.authorKind === "system" || b.authorKind === "system") return false;
      if (a.authorKind !== b.authorKind) return false;
      if ((a.authorUserId ?? null) !== (b.authorUserId ?? null)) return false;
      return (
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime() <
        GROUP_GAP_MS
      );
    };

    return {
      message,
      startsGroup: daySeparator !== null || !continues(previous, message),
      endsGroup:
        next === undefined ||
        !isSameDay(new Date(message.createdAt), new Date(next.createdAt)) ||
        !continues(message, next),
      daySeparator,
    };
  });
}
